import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { UtilService } from '../util/util.service';
import { StorageService } from '../storage/storage.service';
import { AppUser } from 'src/app/models/appUser.model';
import { ProfileUser } from 'src/app/models/profileUser.model';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { UserType } from 'src/app/models/userType.model';
import { RinknetPlayerInformation } from 'src/app/models/rnPlayerInformation.model';
import { PlayerProfileField } from 'src/app/models/playerProfileField.model';

@Injectable({
  providedIn: 'root'
})
export class AppuserService {
  appUser: AppUser = new AppUser();

  checkedAppVersion: boolean = false;

  tileSwitch: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private utilService: UtilService,
    private settings: AppSettingsService,
  ) { }

  getAppUserFromDB(): Observable<AppUser> {
    const url = this.settings.apiUrl + `appUser`;

    return this.http.get(url).pipe(
      map((data: any) => {
        if (data.status != "ok") {
          return null;
        } else {
          this.appUser = data.app_user;
          this.storage.set("app_user", this.appUser);
          return new AppUser(data.app_user);
        }
      })
    );
  }

  getAppUser(): Promise<AppUser> {
    return this.storage.get("app_user").then((appUser: AppUser) => {
      if(appUser && appUser.id) {
        return new AppUser(appUser);
      } else {
        const url = this.settings.apiUrl + `appUser`;

        let observable: Observable<AppUser> = this.http.get(url).pipe(
          map((data: any) => {
            if (data.status != "ok") {
              return null;
            } else {
              this.appUser = data.app_user;
              this.storage.set("app_user", this.appUser);
              return new AppUser(data.app_user);
            }
          })
        );
        return observable.toPromise();
      }
    });
  }

  getAppUserByPlayerRNID(playerRNID: number): Observable<AppUser> {
    const params = {
      player_rnid: playerRNID
    }

    const url = this.settings.apiUrl + `appUsers?parameters=${JSON.stringify(params)}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let appUser: AppUser = new AppUser();
        if(data && data.length > 0) {
          appUser = new AppUser(data[0]);
        }
        return appUser;
      })
    );
  }

  updateAppUserSettings(appearInSearch: boolean, showPortalScroller: boolean, showRecommended: boolean): Observable<AppUser> {
    const body = {
      appear_in_search: appearInSearch,
      show_portal_scroller: showPortalScroller,
      show_recommended: showRecommended
    }

    const url = this.settings.apiUrl + `appUser/settings`;

    return this.http.put(url, body).pipe(
      map((data: AppUser) => {
        this.appUser = data;
        this.storage.set("app_user", this.appUser);
        return new AppUser(data);
      })
    );
  }

  updateAppUser(email: string, password: string, oldPassword: string, cellNumber: string): Observable<AppUser> {
    const body = {
      email: email,
      password: password,
      old_password: oldPassword,
      phone_number: cellNumber
    }

    const url = this.settings.apiUrl + `appUser`;

    return this.http.put(url, body).pipe(
      map((data: AppUser) => {
        this.appUser = data;
        this.storage.set("app_user", this.appUser);
        return new AppUser(data);
      })
    );
  }

  updateAppUserSetup(userTypeId: number, isFemale: boolean, birthdate: string, hometown: string, isRinknetUser: boolean, rinknetAuthToken: string, rinknetAttachmentID: number): Observable<AppUser> {
    const body = {
      user_type_id: userTypeId,
      is_female: isFemale,
      birthdate: birthdate,
      hometown: hometown,
      is_rinknet_user: isRinknetUser,
      rinknet_auth_token: rinknetAuthToken,
      rinknet_user_service_id: rinknetAttachmentID
    }

    const url = this.settings.apiUrl + `appUser/userSetup`;

    return this.http.put(url, body).pipe(
      map((data: AppUser) => {
        this.appUser = data;
        this.storage.set("app_user", this.appUser);
        return new AppUser(data);
      })
    );
  }

  updateAppUserName(firstName: string, lastName: string, isPlayer: boolean) {
    const body = {
      first_name: firstName,
      last_name: lastName,
      is_player: isPlayer
    }

    const url = this.settings.apiUrl + `appUser/name`;

    return this.http.put(url, body).pipe(
      map(() => {
        this.storage.remove("app_user");
      })
    );
  }

  getAppUserByID(appUserID: number): Observable<[string, AppUser]> {
    const url = this.settings.apiUrl + `appUsers/${appUserID}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let appUser: AppUser = null;
        if (data.app_user) {
          appUser = new AppUser(data.app_user);
        }
        return [data.status, appUser];
      })
    );
  }

  queryAppUser(): Observable<AppUser> {
    const url = this.settings.apiUrl + `appUser`;

    return this.http.get(url).pipe(
      map((data: any) => {
        this.appUser = data;
        this.storage.set("app_user", this.appUser);
        return new AppUser(data);
      })
    );
  }

  searchAppUsers(query: string, isRecruit: boolean, filters: any): Observable<ProfileUser[]> {
    const params = {
      query: this.utilService.jsonFriendlyUrlString(query),
      is_subscribed: isRecruit,
      jersey: null,
      league_rnid: null,
      country_rnid: null,
      position_rnid: null,
      min_height: null,
      max_height: null,
      min_weight: null,
      max_weight: null,
      team_rnid: null,
      birthdate: null
    }

    if(filters) {
      params.jersey = filters.jersey;
      params.league_rnid = filters.league;
      params.country_rnid = filters.nationality;
      params.position_rnid = filters.position;
      params.team_rnid = filters.team;
      params.birthdate = filters.date;

      if(filters.height) {
        params.min_height = filters.height.lower;
        params.max_height = filters.height.upper;
      } else {
        params.min_height = null;
        params.max_height = null;
      }

      if(filters.weight) {
        params.min_weight = filters.weight.lower;
        params.max_weight = filters.weight.upper;
      } else {
        params.min_weight = null;
        params.max_weight = null;
      }
    }

    const url = this.settings.apiUrl + `search/appUser?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let profileUsers: ProfileUser[] = [];
        if(data) {
          data.forEach(profileUser => {
            profileUsers.push(new ProfileUser(profileUser))
          });
        }

        return profileUsers;
      })
    );
  }

  deleteAppUser() : Observable<AppUser> {
    const url = this.settings.apiUrl + 'appUser';

    return this.http.delete(url).pipe(
      map((data: any) => {
        return new AppUser(data);
      })
    );
  }

  logAccountChange(accountSetting: string) {
    const body = {
      account_setting: accountSetting
    }

    const url = this.settings.apiUrl + `accountSettingChangeLog`;

    return this.http.post(url, body);
  }

  getUserTypes() : Observable<UserType[]> {
    const url = this.settings.apiUrl + 'userTypes';

    return this.http.get(url).pipe(
      map((data: any) => {
        let userTypes: UserType[] = [];
        if(data) {
          data.forEach(userType => {
            userTypes.push(new UserType(userType))
          });
        }
        
        return userTypes;
      })
    );
  }

  getUserPlayerInformation(): Observable<RinknetPlayerInformation> {
    const url = this.settings.apiUrl + `appUser/playerinfo`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return new RinknetPlayerInformation(data);
      })
    );
  }

  updateUserPlayerInformation(player: RinknetPlayerInformation): Observable<[RinknetPlayerInformation, number, PlayerProfileField[]]> {
    const url = this.settings.apiUrl + `appUser/playerinfo`;
    let payload = {
      player_information: player
    };

    return this.http.put(url, payload).pipe(
      map((data: any) => {
        let playerInfo = new RinknetPlayerInformation(data.player_information);
        let dataMissingFields = data.missing_fields;
        let missingFields: PlayerProfileField[] = [];
        let progress = data.profile_progress;

        dataMissingFields.forEach(field => {
          missingFields.push(new PlayerProfileField(field));
        });
        
        return [playerInfo, progress, missingFields];
      })
    );
  }

  addAppUserRevenueCatID(revenuecatID?: string): Promise<string> {
    let id: string;
    if (revenuecatID) {
      id = revenuecatID;
    } else {
      id = "";
    }
    const body = {
      revenuecat_id: id
    }

    const url = this.settings.apiUrl + `appUser/revenuecatID`;

    return firstValueFrom(this.http.put(url, body).pipe(
      map((data: any) => {
        this.storage.remove("app_user"); // remove old app user info
        if (data.revenuecat_id) {
          return data.revenuecat_id;
        } else {
          return null;
        }
      })
    ));
  }

  getTotalPostViews(): Observable<number> {
    const url = this.settings.apiUrl + `appUser/totalPostViews`;
    return this.http.get(url).pipe(
      map((total: number) => {
        return total;
      })
    )
  }

  // Get total profile views for a specific app user
  getTotalPostViewsByAppUser(appUserID: number): Observable<number> {
    const url = this.settings.apiUrl + `appUsers/${appUserID}/totalPostViews`;
    return this.http.get(url).pipe(
      map((total: number) => {
        return total;
      })
    )
  }

  // Get total profile views for the current app user
  getTotalProfileViews(): Observable<number> {
    const url = this.settings.apiUrl + `appUser/totalProfileViews`;
    return this.http.get(url).pipe(
      map((total: number) => {
        return total;
      })
    )
  }

  // Get total profile views for a specific app user
  getTotalProfileViewsByAppUser(appUserID: number): Observable<number> {
    const url = this.settings.apiUrl + `appUsers/${appUserID}/totalProfileViews`;
    return this.http.get(url).pipe(
      map((total: number) => {
        return total;
      })
    )
  }
}
